// frontend/src/hooks/useModelCreditCosts.ts
//
// Fetches per-model credit costs from the miniapp_llm_models table.
// The data is public (anon has SELECT) and cached for the session lifetime
// since model costs change very rarely.

import { useState, useEffect } from 'react';

export type ModelCreditCosts = Record<string, number>;

// Module-level cache — fetched once per page load
let cachedCosts: ModelCreditCosts | null = null;
let fetchPromise: Promise<ModelCreditCosts> | null = null;

async function fetchModelCosts(): Promise<ModelCreditCosts> {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

  const response = await fetch(
    `${supabaseUrl}/rest/v1/miniapp_llm_models?select=model_keys,credit_cost`,
    {
      headers: {
        'apikey': anonKey,
        'Authorization': `Bearer ${anonKey}`,
      },
    }
  );

  if (!response.ok) {
    console.error('Failed to fetch model credit costs:', response.status);
    return {};
  }

  const rows: { model_keys: string[]; credit_cost: number }[] = await response.json();
  const costs: ModelCreditCosts = {};

  for (const row of rows) {
    for (const key of row.model_keys) {
      costs[key] = row.credit_cost;
    }
  }

  return costs;
}

export function useModelCreditCosts(): ModelCreditCosts {
  const [costs, setCosts] = useState<ModelCreditCosts>(cachedCosts || {});

  useEffect(() => {
    if (cachedCosts) {
      setCosts(cachedCosts);
      return;
    }

    if (!fetchPromise) {
      fetchPromise = fetchModelCosts().then((result) => {
        cachedCosts = result;
        return result;
      });
    }

    fetchPromise.then((result) => {
      setCosts(result);
    });
  }, []);

  return costs;
}
